import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Check, X, ArrowRight, Loader2, MapPin, Sparkles,
  PlusCircle, MinusCircle, AlertTriangle,
} from "lucide-react";
import type { ChatChange } from "../api/client";
import { fieldToTab, displayField } from "../utils/changeLocation";
import { fmtFieldValue } from "../utils/fieldValueFormat";

interface Props {
  changes: ChatChange[];
  summary?: string;
  scenarioName?: string;
  applying: boolean;
  onApply: () => void;
  onCancel: () => void;
}

interface GroupedChanges {
  key: string;
  tabName: string;
  section: string;
  changes: ChatChange[];
}

function groupByLocation(changes: ChatChange[]): GroupedChanges[] {
  const groups: GroupedChanges[] = [];
  const index = new Map<string, GroupedChanges>();
  for (const c of changes) {
    const loc = fieldToTab(c);
    const key = `${loc.tabName}|${loc.section}`;
    let g = index.get(key);
    if (!g) {
      g = { key, tabName: loc.tabName, section: loc.section, changes: [] };
      index.set(key, g);
      groups.push(g);
    }
    g.changes.push(c);
  }
  return groups;
}

function isMetaAction(c: ChatChange): boolean {
  return (c.field_name || "").startsWith("_");
}

function metaIcon(field: string) {
  if (field.startsWith("_add") || field === "_create_profile")
    return <PlusCircle className="w-4 h-4 text-forest-600 shrink-0" />;
  if (field.startsWith("_remove"))
    return <MinusCircle className="w-4 h-4 text-red-500 shrink-0" />;
  return <Sparkles className="w-4 h-4 text-honey-500 shrink-0" />;
}

/** One change row: field label, tenant chip, before → after diff. */
function ChangeRow({ change }: { change: ChatChange }) {
  const field = change.field_name || "";
  const hasOld = change.old_value !== undefined && change.old_value !== null;

  return (
    <div className="px-4 py-3 bg-white">
      <div className="flex items-center gap-2 mb-1.5">
        {isMetaAction(change) && metaIcon(field)}
        <span className="text-sm font-medium text-bark-800">{displayField(field)}</span>
        {change.tenant_name && (
          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-md bg-forest-50 text-forest-700 border border-forest-100">
            {change.tenant_name}
          </span>
        )}
        {change.is_document_override && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-md bg-honey-50 text-honey-700 border border-honey-200">
            <AlertTriangle className="w-2.5 h-2.5" /> Overrides document data
          </span>
        )}
      </div>
      <div className="flex items-center gap-2.5">
        {hasOld && (
          <>
            <span className="px-2 py-1 rounded-md bg-red-50 border border-red-100 text-sm text-red-700/80 line-through decoration-red-300 tabular-nums whitespace-nowrap">
              {fmtFieldValue(change.old_value, field)}
            </span>
            <ArrowRight className="w-3.5 h-3.5 text-bark-300 shrink-0" />
          </>
        )}
        <span className="px-2 py-1 rounded-md bg-green-50 border border-green-200 text-sm font-semibold text-forest-800 tabular-nums whitespace-nowrap">
          {fmtFieldValue(change.new_value, field)}
        </span>
      </div>
      {change.reason && (
        <p className="mt-1.5 text-xs text-bark-400 italic">{change.reason}</p>
      )}
    </div>
  );
}

/**
 * Change wizard: pre-apply confirmation modal for chat-driven model changes.
 * Shows exactly what will change (before → after, grouped by the area of the
 * model impacted) and waits for an explicit Apply or Cancel. Nothing is
 * written to the model until Apply.
 */
export default function ChangeConfirmationModal({
  changes, summary, scenarioName, applying, onApply, onCancel,
}: Props) {
  const groups = useMemo(() => groupByLocation(changes), [changes]);
  const n = changes.length;

  // Esc cancels, Enter applies — capture phase so the chat textarea
  // underneath never sees these keys while the modal is up.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (!applying) onCancel();
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        if (!applying) onApply();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [applying, onApply, onCancel]);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div className="relative bg-white rounded-2xl shadow-2xl border border-grove-200 w-full max-w-xl mx-4 max-h-[82vh] flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-grove-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-forest-100 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-forest-600" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-bark-800 leading-tight">
                Review {n === 1 ? "Change" : `${n} Changes`}
              </h2>
              <p className="text-xs text-bark-400 mt-0.5">
                {scenarioName ? `Scenario: ${scenarioName} — nothing` : "Nothing"} is applied until you confirm.
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            disabled={applying}
            className="p-1.5 rounded-lg text-bark-300 hover:text-bark-500 hover:bg-grove-100 transition-colors disabled:opacity-40"
            title="Cancel (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Summary from the interpreter */}
        {summary && (
          <div className="px-6 py-3 bg-grove-50/70 border-b border-grove-200">
            <p className="text-sm text-bark-600">{summary}</p>
          </div>
        )}

        {/* Grouped change list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {groups.map(g => (
            <div key={g.key}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <MapPin className="w-3 h-3 text-forest-600" />
                <span className="text-[11px] font-semibold text-forest-800 uppercase tracking-wider">
                  {g.tabName}
                </span>
                <span className="text-[11px] text-bark-400">› {g.section}</span>
              </div>
              <div className="rounded-xl border border-grove-200 overflow-hidden divide-y divide-grove-100">
                {g.changes.map((c, i) => <ChangeRow key={i} change={c} />)}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-grove-200 bg-grove-50/50 rounded-b-2xl">
          <p className="text-xs text-bark-400">
            Projections recalculate after applying. You can undo from the chat.
          </p>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            <button
              onClick={onCancel}
              disabled={applying}
              className="px-4 py-2 text-sm font-medium text-bark-600 bg-white border border-grove-300 rounded-xl hover:bg-grove-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onApply}
              disabled={applying}
              className="flex items-center gap-1.5 px-4 py-2 bg-forest-600 text-white text-sm font-semibold rounded-xl hover:bg-forest-700 transition-colors shadow-sm disabled:opacity-60"
            >
              {applying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Applying…
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" /> Apply {n === 1 ? "Change" : `${n} Changes`}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
